// src/pages/Home.js
//
// REESCRITO 03-oct-26 — Giro de foco: los cursos propios de la
// Federación son el protagonista; las alianzas pasan a ser una
// opción secundaria al final del landing.
//
// Jerarquía nueva de la página:
//   1. Hero: invitación a formarse con los cursos de FENAGRO.
//   2. Catálogo destacado: tarjetas de los cursos oficiales
//      (Curso Básico de Economía Solidaria + próximos).
//   3. Quiénes somos (breve): identidad de la federación.
//   4. Cómo aprendes: flujo del estudiante en 4 pasos.
//   5. Qué incluye la plataforma: features alineadas al estudiante.
//   6. Para instituciones aliadas: bloque secundario y discreto.
//   7. CTA final.
//
// Usa el theme REAL de FENAGRO (fenagroTheme.js) envuelto en un
// ThemeProvider local — redundante con el global que ya aplica en
// modo Fenagro, se mantiene por compatibilidad y para que el Home
// se vea igual si se visita desde otra app.

import React from 'react';
import { Link } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  School,
  VideoCall,
  WorkspacePremium,
  Groups,
  Forum,
  CheckCircle,
  ArrowForward,
  AccountBalance,
  AutoStories,
  TrendingUp,
  Gavel,
  AccessTime,
  SignalCellularAlt,
} from '@mui/icons-material';
import fenagroTheme from '../theme/fenagroTheme';
import fenagroLogo from '../assets/fenagro-logo.png';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AppContext';

// ─── Catálogo destacado (hardcoded hasta tener catálogo en BD) ────
// El primero (Economía Solidaria) ya existe como material oficial
// (ver 'Curso de Educacion Solidaria FENAGRO APL 1 Julio 2025.pptx').
// Los otros dos son los siguientes del roadmap de contenidos.

const CURSOS_DESTACADOS = [
  {
    icono: AutoStories,
    nivel: 'Básico',
    duracion: '16 horas · 10 módulos',
    estado: 'disponible',   // disponible | proximamente
    slug: 'economia-solidaria-basico',
    titulo: 'Curso Básico de Economía Solidaria',
    descripcion:
      'Introducción al modelo cooperativo, principios del sector solidario, '
      + 'marco jurídico colombiano (Ley 454 de 1998) y portafolio de servicios '
      + 'de la Federación. Con certificación oficial al aprobar el 100%.',
    temas: [
      'Quiénes somos y misión del sector solidario',
      'Contexto socio-económico del siglo XXI',
      'Modelos empresariales solidarios',
      'Marco normativo colombiano',
    ],
  },
  {
    icono: TrendingUp,
    nivel: 'Intermedio',
    duracion: '24 horas · 12 módulos',
    estado: 'proximamente',
    titulo: 'Emprendimiento y Economía Digital Solidaria',
    descripcion:
      'Modelos empresariales del siglo XXI, los cinco cuadrantes económicos, '
      + 'transformación digital en el sector solidario, plataformas colaborativas '
      + 'y generación de activos digitales con fidelización de redes.',
    temas: [
      'Los cinco cuadrantes económicos',
      'Comercio digital y plataformas colaborativas',
      'Fidelización y minería de datos',
      'Caso práctico: tu propio emprendimiento solidario',
    ],
  },
  {
    icono: Gavel,
    nivel: 'Avanzado',
    duracion: '32 horas · 14 módulos',
    estado: 'proximamente',
    titulo: 'Marco Normativo del Sector Solidario Colombiano',
    descripcion:
      'Profundización legal: cooperativas, pre-cooperativas, asociaciones mutualistas, '
      + 'fondos de empleados, administraciones públicas cooperativas y cooperativas '
      + 'de trabajo asociado. Dirigido a gestores y revisores fiscales del sector.',
    temas: [
      'Ley 79 de 1988 y Ley 454 de 1998',
      'Régimen por tipo de organización solidaria',
      'Normativa general transversal',
      'Supersolidaria y Circular Básica Jurídica',
    ],
  },
];

// ─── Features de la plataforma (reordenadas al foco "estudiante") ─

const FEATURES = [
  {
    icon: VideoCall,
    title: 'Clases en vivo y grabadas',
    description: 'Sesiones sincrónicas con el mentor y acceso a grabaciones para repasar a tu ritmo.',
  },
  {
    icon: WorkspacePremium,
    title: 'Certificación oficial',
    description: 'Al aprobar el 100% del curso recibes un certificado digital con folio único y verificación pública.',
  },
  {
    icon: Groups,
    title: 'Mentor dedicado',
    description: 'Un mentor te acompaña durante todo el curso en las prácticas y la Escuela de Emprendedores Solidarios.',
  },
  {
    icon: AutoStories,
    title: 'Material multimedia',
    description: 'Textos, videos, lecturas, cuestionarios e infografías por cada unidad temática.',
  },
  {
    icon: Forum,
    title: 'Chat y foros por curso',
    description: 'Comunicación directa con el mentor y foros de discusión moderados por módulo.',
  },
  {
    icon: School,
    title: 'Evaluación integral',
    description: 'Quizzes con calificación automática, actividades prácticas y entregas evaluadas por rúbricas.',
  },
];

// ─── Componente principal ─────────────────────────────────────────

function HomeContent() {
  const { isAuthenticated } = useAuth();

  return (
    <Box sx={{ bgcolor: '#ffffff' }}>
      {/* ═══════════════════════ HERO ═══════════════════════ */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a4d2e 0%, #2e7d32 60%, #43a047 100%)',
          color: '#fff',
          pt: { xs: 10, md: 14 },
          pb: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, #c8a84b, #f0d070, #c8a84b)',
          height: 4, top: 'auto', bottom: 0,
        }} />

        <Container maxWidth="md" sx={{ textAlign: 'center', position: 'relative' }}>
          <Box component="img" src={fenagroLogo} alt="FENAGRO APL" sx={{ height: 90, mb: 3 }} />

          <Typography
            variant="overline"
            sx={{ color: 'rgba(255,255,255,0.75)', letterSpacing: '0.2em', mb: 2, display: 'block' }}
          >
            Confederación Alianza Solidaria · Federación FENAGRO APL
          </Typography>

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.2rem', md: '3.4rem' },
              mb: 3,
              color: '#fff',
              lineHeight: 1.15,
            }}
          >
            El Aula Virtual de la Red
            <Box component="span" sx={{ display: 'block', color: '#f0d070' }}>
              FENAGRO APL
            </Box>
          </Typography>

          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)', mb: 5, fontSize: '1.1rem', maxWidth: 640, mx: 'auto' }}>
            La plataforma educativa de la Federación — cursos oficiales de la red solidaria
            e instituciones afiliadas que gestionan su propia formación, todo bajo la misma
            infraestructura tecnológica.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/courses"
              variant="contained"
              size="large"
              color="secondary"
              endIcon={<ArrowForward />}
              sx={{ px: 4, py: 1.5, fontWeight: 700 }}
            >
              Ver cursos
            </Button>
            {isAuthenticated ? (
              <Button
                component={Link}
                to="/dashboard"
                variant="outlined"
                size="large"
                sx={{
                  px: 4, py: 1.5, color: '#fff', borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Ir a mi panel
              </Button>
            ) : (
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                size="large"
                sx={{
                  px: 4, py: 1.5, color: '#fff', borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Iniciar sesión
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════ CURSOS DESTACADOS ═══════════════════════ */}
      <Box sx={{ bgcolor: '#f9fbe7', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{ color: '#2e7d32', letterSpacing: '0.2em', mb: 1, display: 'block' }}
            >
              Catálogo oficial de la Federación
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 2, color: '#1a3a1e' }}>
              Nuestros cursos
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 620, mx: 'auto' }}>
              Formación integral en economía solidaria, cooperativismo y emprendimiento,
              diseñada por expertos de FENAGRO APL con validez certificada.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {CURSOS_DESTACADOS.map((curso) => {
              const Icono = curso.icono;
              const disponible = curso.estado === 'disponible';
              return (
                <Grid item xs={12} md={4} key={curso.titulo}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderTop: '4px solid',
                      borderTopColor: disponible ? '#2e7d32' : '#c8a84b',
                      transition: 'transform 180ms ease, box-shadow 180ms ease',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                    }}
                  >
                    <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                        <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', width: 48, height: 48 }}>
                          <Icono />
                        </Avatar>
                        {disponible ? (
                          <Chip
                            label="Disponible"
                            size="small"
                            sx={{ bgcolor: '#2e7d32', color: '#fff', fontWeight: 600 }}
                          />
                        ) : (
                          <Chip
                            label="Próximamente"
                            size="small"
                            sx={{ bgcolor: '#f0d070', color: '#1a3a1e', fontWeight: 600 }}
                          />
                        )}
                      </Stack>

                      <Typography variant="h6" sx={{ color: '#1a3a1e', mb: 1.5, lineHeight: 1.35 }}>
                        {curso.titulo}
                      </Typography>

                      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <SignalCellularAlt sx={{ fontSize: 16, color: '#5a6a51' }} />
                          <Typography variant="caption" color="text.secondary">{curso.nivel}</Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <AccessTime sx={{ fontSize: 16, color: '#5a6a51' }} />
                          <Typography variant="caption" color="text.secondary">{curso.duracion}</Typography>
                        </Stack>
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                        {curso.descripcion}
                      </Typography>

                      <Divider sx={{ mb: 2 }} />

                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a3a1e', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 1, display: 'block' }}>
                        Contenido
                      </Typography>
                      <Stack spacing={0.75} sx={{ mb: 2.5, flexGrow: 1 }}>
                        {curso.temas.map((tema) => (
                          <Stack key={tema} direction="row" spacing={1} alignItems="flex-start">
                            <CheckCircle sx={{ fontSize: 16, color: '#2e7d32', mt: '2px' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                              {tema}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>

                      <Button
                        component={Link}
                        to={disponible ? (curso.slug ? `/course/${curso.slug}` : '/courses') : '#'}
                        variant={disponible ? 'contained' : 'outlined'}
                        color={disponible ? 'primary' : 'inherit'}
                        fullWidth
                        disabled={!disponible}
                        endIcon={disponible ? <ArrowForward /> : null}
                        sx={{ mt: 'auto', fontWeight: 600 }}
                      >
                        {disponible ? 'Más información' : 'Pronto disponible'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Typography variant="body2" color="text.secondary">
              Catálogo en expansión — nuevos cursos cada trimestre
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════ QUIÉNES SOMOS ═══════════════════════ */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.3rem' }, mb: 3, color: '#1a3a1e' }}>
          Educación integral del sector solidario
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.75 }}>
          Somos una federación nacional del sector solidario comprometida con la formación
          integral — ser, saber y saber hacer — en cooperativismo, economía solidaria y
          emprendimiento digital. A través de nuestra Aula Virtual ofrecemos cursos con
          validez certificada, mentor dedicado y acompañamiento práctico, pensados para
          quienes construyen el modelo económico del siglo XXI.
        </Typography>
      </Container>

      {/* ═══════════════════════ CÓMO APRENDES ═══════════════════════ */}
      <Box sx={{ bgcolor: '#f1f8e9', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.2rem' }, mb: 5, textAlign: 'center', color: '#1a3a1e' }}>
            Cómo aprendes con nosotros
          </Typography>
          <Grid container spacing={4}>
            {[
              { n: '1', text: 'Explora el catálogo oficial de cursos de la Federación.' },
              { n: '2', text: 'Inscríbete al curso que necesites — con pago en línea seguro.' },
              { n: '3', text: 'Aprende con módulos teóricos, prácticas y clases en vivo con tu mentor.' },
              { n: '4', text: 'Aprueba las evaluaciones y recibe tu certificación oficial descargable.' },
            ].map((step) => (
              <Grid item xs={12} sm={6} key={step.n}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Avatar sx={{
                    background: 'linear-gradient(135deg,#1a4d2e,#43a047)',
                    width: 40, height: 40, fontSize: '1rem', fontWeight: 700, flexShrink: 0,
                  }}>
                    {step.n}
                  </Avatar>
                  <Typography variant="body1" color="text.primary" sx={{ pt: 0.75, lineHeight: 1.6 }}>
                    {step.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ═══════════════════════ QUÉ INCLUYE LA PLATAFORMA ═══════════════════════ */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.3rem' }, mb: 2, color: '#1a3a1e' }}>
            Qué incluye tu formación
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
            Cada curso viene con todo lo necesario para una experiencia completa de aprendizaje.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {FEATURES.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.title}>
              <Card sx={{ height: '100%', boxShadow: 1 }}>
                <CardContent sx={{ p: 3 }}>
                  <Avatar sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', width: 52, height: 52, mb: 2 }}>
                    <feature.icon />
                  </Avatar>
                  <Typography variant="h6" sx={{ mb: 1, color: '#1a3a1e', fontSize: '1.1rem' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ═══════════════════════ PARA INSTITUCIONES ALIADAS (secundario) ═══════════════════════ */}
      <Box sx={{ bgcolor: '#f9fbe7', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Card sx={{ boxShadow: 2, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
              <Box sx={{
                bgcolor: '#1a4d2e',
                color: '#fff',
                p: { xs: 3, md: 4 },
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minWidth: { md: 180 },
              }}>
                <AccountBalance sx={{ fontSize: 56, color: '#f0d070' }} />
              </Box>
              <CardContent sx={{ p: { xs: 3, md: 4 }, flex: 1 }}>
                <Typography variant="overline" sx={{ color: '#5a6a51', letterSpacing: '0.15em' }}>
                  Para instituciones
                </Typography>
                <Typography variant="h5" sx={{ color: '#1a3a1e', mb: 1.5, mt: 0.5 }}>
                  ¿Tu institución quiere ofrecer sus propios cursos?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                  Colegios, universidades y cooperativas educativas pueden sumarse como instituciones
                  aliadas de la red FENAGRO APL y gestionar su propio PEI, cursos, matrículas y pensiones
                  dentro de la plataforma.
                </Typography>
                <Button
                  component={Link}
                  to="/instituciones/inscripcion"
                  variant="outlined"
                  color="primary"
                  endIcon={<ArrowForward />}
                  sx={{ fontWeight: 600 }}
                >
                  Conocer el programa de alianzas
                </Button>
              </CardContent>
            </Box>
          </Card>
        </Container>
      </Box>

      {/* ═══════════════════════ CTA FINAL ═══════════════════════ */}
      <Box sx={{ background: 'linear-gradient(135deg, #1a4d2e, #2e7d32)', py: { xs: 8, md: 10 }, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <CheckCircle sx={{ fontSize: 48, color: '#f0d070', mb: 2 }} />
          <Typography variant="h3" sx={{ color: '#fff', fontSize: { xs: '1.6rem', md: '2rem' }, mb: 3 }}>
            Comienza tu formación en economía solidaria
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.85)', mb: 4 }}>
            Explora el catálogo oficial y matricúlate en el curso que necesites.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              component={Link}
              to="/courses"
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<ArrowForward />}
              sx={{ px: 5, py: 1.5, fontWeight: 700 }}
            >
              Ver todos los cursos
            </Button>
            {!isAuthenticated && (
              <Button
                component={Link}
                to="/login"
                variant="outlined"
                size="large"
                sx={{
                  px: 5, py: 1.5,
                  color: '#fff', borderColor: 'rgba(255,255,255,0.5)',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                Iniciar sesión
              </Button>
            )}
          </Stack>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

function HomePage() {
  return (
    <ThemeProvider theme={fenagroTheme}>
      <HomeContent />
    </ThemeProvider>
  );
}

export default HomePage;
